// Catalog of admin-editable text, the source of truth for which keys exist,
// where each one is shown and what it says until an admin changes it.
//
// Rows live in public.site_copy (key, tenant_id NULL = every site, value).
// Resolution: the city's row → the global row → the default below, with
// {siteName} {city} {region} {country} filled from the tenant. With no rows
// every page renders exactly the literal it rendered before.
//
// Two families of keys:
//   social.*  share title / description used for og:* and twitter:* tags
//             (edited under Admin → Site assets → Social sharing)
//   home.* about.* owners.*  marketing copy (Admin → Site copy)

export type SiteCopyPage = "home" | "about" | "owners" | "areas" | "pricing" | "social" | "seo" | "deck" | "ownerTips" | "messages";

export interface SiteCopySpec {
  /** Stable identifier, matches the `key` column. Never rename: rows are addressed by it. */
  key: string;
  page: SiteCopyPage;
  /** Grouping inside the page ("Hero", "How it works", …). */
  section: string;
  /** Field label in the admin. */
  label: string;
  /** Today's literal, with {placeholders}. "" = "use the page's own text" (social keys). */
  default: string;
  multiline?: boolean;
  /** Newline-separated bullets, read back with splitCopyList(). */
  list?: boolean;
  maxLength?: number;
  hint?: string;
}

/** Marketing pages the Site copy tab edits (social keys are edited elsewhere). */
export const SITE_COPY_PAGES: { id: Exclude<SiteCopyPage, "social">; label: string; path: string }[] = [
  { id: "home", label: "Home", path: "/" },
  { id: "about", label: "About", path: "/about" },
  { id: "owners", label: "For owners", path: "/owner-onboarding" },
  { id: "areas", label: "Explore areas", path: "/areas" },
  { id: "pricing", label: "Extras (pricing)", path: "/pricing" },
  { id: "ownerTips", label: "Owner pages (tips)", path: "/dashboard" },
  { id: "messages", label: "Requests and chats", path: "/messages" },
];

// ---- Social sharing ---------------------------------------------------------
// One share card + share text per public page, plus a site-wide default.

export const SOCIAL_PAGES = [
  { id: "home", path: "/", label: "Home" },
  { id: "properties", path: "/properties", label: "Browse rentals" },
  { id: "areas", path: "/areas", label: "Explore areas" },
  { id: "about", path: "/about", label: "About" },
  { id: "owners", path: "/owner-onboarding", label: "List your home" },
  { id: "contact", path: "/contact", label: "Contact" },
] as const;

export type SocialPageId = (typeof SOCIAL_PAGES)[number]["id"];

export const isSocialPageId = (id: string): id is SocialPageId => SOCIAL_PAGES.some((p) => p.id === id);

export const socialPage = (id: SocialPageId) => SOCIAL_PAGES.find((p) => p.id === id)!;

export const SOCIAL_SITE_TITLE_KEY = "social.site.title";
export const SOCIAL_SITE_DESCRIPTION_KEY = "social.site.description";
export const socialTitleKey = (id: SocialPageId): string => `social.page.${id}.title`;
export const socialDescriptionKey = (id: SocialPageId): string => `social.page.${id}.description`;

/** Facebook/WhatsApp clip titles around 60–70 characters and descriptions around 200. */
export const SHARE_TITLE_MAX = 70;
export const SHARE_DESCRIPTION_MAX = 200;

/** Site-wide share text: the app shell and any page without its own title. */
export const SOCIAL_SITE_TITLE_DEFAULT = "{siteName} | Monthly & Seasonal Rentals in {country}";
export const SOCIAL_SITE_DESCRIPTION_DEFAULT =
  "Find monthly and seasonal home rentals in {city} and {region}, {country}. Real prices, clear amenities, live availability.";

const SOCIAL_COPY: SiteCopySpec[] = [
  {
    key: SOCIAL_SITE_TITLE_KEY,
    page: "social",
    section: "Site default",
    label: "Share title",
    default: SOCIAL_SITE_TITLE_DEFAULT,
    maxLength: SHARE_TITLE_MAX,
    hint: "Shown when a link to the site as a whole is shared, and for pages without a title of their own.",
  },
  {
    key: SOCIAL_SITE_DESCRIPTION_KEY,
    page: "social",
    section: "Site default",
    label: "Share description",
    default: SOCIAL_SITE_DESCRIPTION_DEFAULT,
    multiline: true,
    maxLength: SHARE_DESCRIPTION_MAX,
  },
  ...SOCIAL_PAGES.flatMap((p): SiteCopySpec[] => [
    {
      key: socialTitleKey(p.id),
      page: "social",
      section: p.label,
      label: "Share title",
      default: "",
      maxLength: SHARE_TITLE_MAX,
      hint: "Leave empty to share the page's own title.",
    },
    {
      key: socialDescriptionKey(p.id),
      page: "social",
      section: p.label,
      label: "Share description",
      default: "",
      multiline: true,
      maxLength: SHARE_DESCRIPTION_MAX,
      hint: "Leave empty to share the page's own description.",
    },
  ]),
];

// ---- Page titles and descriptions --------------------------------------------
// What Google, the browser tab and (unless overridden above) shared links show
// for each public page. Defaults are the strings the pages used to hard-code;
// {areasShort} {areaCount} {areaList} come from the tenant's Locations and
// {pricing} from owners.pricing.claim.

export const seoTitleKey = (id: SocialPageId): string => `seo.page.${id}.title`;
export const seoDescriptionKey = (id: SocialPageId): string => `seo.page.${id}.description`;

export const PAGE_SEO_DEFAULTS: Record<SocialPageId, { title: string; description: string }> = {
  home: { title: SOCIAL_SITE_TITLE_DEFAULT, description: SOCIAL_SITE_DESCRIPTION_DEFAULT },
  properties: {
    title: "Browse Rentals in {city} & {region}",
    description: "Browse monthly and seasonal rental homes across {areasShort}. Filter by area, dates, budget and amenities.",
  },
  areas: {
    title: "Explore Areas | {city} & {region}",
    description: "Guide to the {areaCount} areas covered by {siteName}: {areaList}.",
  },
  about: {
    title: "About {siteName} | How the Rental Marketplace Works",
    description: "Learn how {siteName} brings structure to monthly and seasonal rentals in {city} and {region}, {country}.",
  },
  owners: {
    title: "List Your {city} Rental | {pricing} | {siteName}",
    description:
      "List your monthly or seasonal rental in {city} and {region}. {pricing} per property. Reach pre-screened renters with full profiles.",
  },
  contact: {
    title: "Contact {siteName} | Support & Partnerships",
    description: "Get in touch with the {siteName} team. Questions, support, or partnership ideas about rentals in {city}.",
  },
};

const SEO_COPY: SiteCopySpec[] = SOCIAL_PAGES.flatMap((p): SiteCopySpec[] => [
  {
    key: seoTitleKey(p.id),
    page: "seo",
    section: p.label,
    label: "Page title",
    default: PAGE_SEO_DEFAULTS[p.id].title,
    maxLength: 70,
    hint: "Browser tab, Google result and the shared link unless a share title is set.",
  },
  {
    key: seoDescriptionKey(p.id),
    page: "seo",
    section: p.label,
    label: "Page description",
    default: PAGE_SEO_DEFAULTS[p.id].description,
    multiline: true,
    maxLength: 200,
  },
]);

/** The owner-landing price claim; appears in that page's title and description. */
export const OWNER_PRICING_KEY = "owners.pricing.claim";

const OWNER_PRICING_COPY: SiteCopySpec[] = [
  {
    key: OWNER_PRICING_KEY,
    page: "owners",
    section: "Pricing",
    label: "Price claim",
    default: "$45/year",
    maxLength: 40,
    hint: "Used as {pricing} in the owner page title and description. Keep it consistent with the badges on the page.",
  },
];

// ---- Marketing copy ---------------------------------------------------------
// Home, About and the owner landing page, defaults are the literals the pages
// used to hard-code; bullets are stored one per line.
const MARKETING_COPY: SiteCopySpec[] = [
  { key: "home.hero.eyebrow", page: "home", section: "Hero", label: "Eyebrow above the city name", default: "Find your home in" },
  { key: "home.hero.ctaFind", page: "home", section: "Hero", label: "Renter button", default: "FIND YOUR HOME" },
  { key: "home.hero.ctaList", page: "home", section: "Hero", label: "Owner button", default: "LIST YOUR HOME" },
  { key: "home.problem.heading", page: "home", section: "The problem", label: "Heading", default: "Rentals in {city} shouldn't be this hard" },
  { key: "home.problem.body", page: "home", section: "The problem", label: "Paragraph", default: "Today, finding a rental means scrolling through WhatsApp chats and Facebook posts. Missing prices, unclear locations, endless back-and-forth questions. It's inefficient for both renters and owners", multiline: true },
  { key: "home.solution.heading", page: "home", section: "The solution", label: "Heading", default: "We bring everything into one place" },
  { key: "home.solution.1.title", page: "home", section: "The solution", label: "Point 1 title", default: "Structured Listings" },
  { key: "home.solution.1.desc", page: "home", section: "The solution", label: "Point 1 text", default: "Real information with prices and included amenities" },
  { key: "home.solution.2.title", page: "home", section: "The solution", label: "Point 2 title", default: "Availability Calendars" },
  { key: "home.solution.2.desc", page: "home", section: "The solution", label: "Point 2 text", default: "See what's actually available before reaching out" },
  { key: "home.solution.3.title", page: "home", section: "The solution", label: "Point 3 title", default: "Smart Matching" },
  { key: "home.solution.3.desc", page: "home", section: "The solution", label: "Point 3 text", default: "Get matched with properties that fit your budget, dates, and needs" },
  { key: "home.renters.eyebrow", page: "home", section: "For renters", label: "Eyebrow", default: "For Renters" },
  { key: "home.renters.heading", page: "home", section: "For renters", label: "Heading", default: "Find exactly what fits" },
  { key: "home.renters.bullets", page: "home", section: "For renters", label: "Bullets", default: "Filter by area, price, dates, amenities, and more\nSee exactly what's included in rent\nMatch score shows how well a property fits you to save you time\nRequest to contact owners directly", multiline: true, list: true },
  { key: "home.renters.cta", page: "home", section: "For renters", label: "Button", default: "FIND YOUR HOME" },
  { key: "home.owners.eyebrow", page: "home", section: "For owners", label: "Eyebrow", default: "For Owners" },
  { key: "home.owners.heading", page: "home", section: "For owners", label: "Heading", default: "List once, stop repeating yourself" },
  { key: "home.owners.bullets", page: "home", section: "For owners", label: "Bullets", default: "Set availability and details in one place\nGet matched with serious renters\nQuality leads. No spam, no tire-kickers", multiline: true, list: true },
  { key: "home.owners.cta", page: "home", section: "For owners", label: "Button", default: "LIST YOUR HOME" },
  { key: "home.how.heading", page: "home", section: "How it works", label: "Heading", default: "How It Works" },
  { key: "home.how.renter.1.title", page: "home", section: "How it works", label: "Renter step 1 title", default: "Create your profile" },
  { key: "home.how.renter.1.desc", page: "home", section: "How it works", label: "Renter step 1 text", default: "Tell us your budget, dates, and preferences" },
  { key: "home.how.renter.2.title", page: "home", section: "How it works", label: "Renter step 2 title", default: "Browse & match" },
  { key: "home.how.renter.2.desc", page: "home", section: "How it works", label: "Renter step 2 text", default: "See match scores and find what fits" },
  { key: "home.how.renter.3.title", page: "home", section: "How it works", label: "Renter step 3 title", default: "Request contact" },
  { key: "home.how.renter.3.desc", page: "home", section: "How it works", label: "Renter step 3 text", default: "Connect directly with the owner" },
  { key: "home.how.owner.1.title", page: "home", section: "How it works", label: "Owner step 1 title", default: "List your property" },
  { key: "home.how.owner.1.desc", page: "home", section: "How it works", label: "Owner step 1 text", default: "Add photos, details, and availability" },
  { key: "home.how.owner.2.title", page: "home", section: "How it works", label: "Owner step 2 title", default: "Get matched" },
  { key: "home.how.owner.2.desc", page: "home", section: "How it works", label: "Owner step 2 text", default: "Renters who fit your property reach out" },
  { key: "home.how.owner.3.title", page: "home", section: "How it works", label: "Owner step 3 title", default: "Connect directly" },
  { key: "home.how.owner.3.desc", page: "home", section: "How it works", label: "Owner step 3 text", default: "Share contact details with serious leads" },
  { key: "home.featured.heading", page: "home", section: "Featured properties", label: "Heading", default: "Featured Properties" },
  { key: "home.featured.viewAll", page: "home", section: "Featured properties", label: "Link", default: "View All" },
  { key: "home.areas.heading", page: "home", section: "Explore by area", label: "Heading", default: "Explore by Area" },
  { key: "home.trust.heading", page: "home", section: "Trust", label: "Heading", default: "Quality-Controlled Marketplace" },
  { key: "home.trust.body", page: "home", section: "Trust", label: "Paragraph", default: "Every listing meets minimum quality standards: real photos, verified details, and availability calendars. No spam, no ghost listings", multiline: true },
  { key: "about.title", page: "about", section: "Title", label: "Page title (H1)", default: "About {siteName}" },
  { key: "about.intro.1", page: "about", section: "Introduction", label: "Paragraph 1", default: "{siteName} was created to make renting and listing homes in {city} easier, clearer, and more efficient for both renters and property owners/managers.", multiline: true },
  { key: "about.intro.2", page: "about", section: "Introduction", label: "Paragraph 2", default: "Today, much of the rental market operates through scattered WhatsApp chats, Facebook groups, and word of mouth. Information is often incomplete, availability unclear, and both sides end up spending unnecessary time going back and forth.", multiline: true },
  { key: "about.intro.3", page: "about", section: "Introduction", label: "Paragraph 3", default: "This platform brings more structure without losing the local, personal nature that makes {city} unique.", multiline: true },
  { key: "about.connect.heading", page: "about", section: "A better way to connect", label: "Heading", default: "A better way to connect" },
  { key: "about.connect.lead", page: "about", section: "A better way to connect", label: "Lead-in", default: "At its core, the platform is built on a simple idea:" },
  { key: "about.connect.quote", page: "about", section: "A better way to connect", label: "Quote", default: "Better information leads to better connections." },
  { key: "about.connect.body", page: "about", section: "A better way to connect", label: "Paragraph", default: "By structuring listings and encouraging more complete renter profiles, it becomes easier to understand fit before starting a conversation.", multiline: true },
  { key: "about.connect.resultLead", page: "about", section: "A better way to connect", label: "Before the list", default: "The result is:" },
  { key: "about.connect.results", page: "about", section: "A better way to connect", label: "Results", default: "fewer unnecessary messages\nmore relevant inquiries\nand a smoother experience for both sides", multiline: true, list: true },
  { key: "about.local.heading", page: "about", section: "Built for the city", label: "Heading", default: "Built for {city}" },
  { key: "about.local.1", page: "about", section: "Built for the city", label: "Paragraph 1", default: "This is not a global marketplace trying to fit every location.", multiline: true },
  { key: "about.local.2", page: "about", section: "Built for the city", label: "Paragraph 2", default: "{siteName} is focused specifically on {city} and the surrounding areas of {region}.", multiline: true },
  { key: "about.local.3", page: "about", section: "Built for the city", label: "Paragraph 3", default: "That focus allows the platform to reflect how the market actually works here, while improving the parts that are currently inefficient.", multiline: true },
  { key: "about.quality.heading", page: "about", section: "Quality over volume", label: "Heading", default: "Quality over volume" },
  { key: "about.quality.1", page: "about", section: "Quality over volume", label: "Paragraph 1", default: "The goal is not to become the largest marketplace, but a more reliable one.", multiline: true },
  { key: "about.quality.lead", page: "about", section: "Quality over volume", label: "Before the list", default: "The platform prioritises:" },
  { key: "about.quality.priorities", page: "about", section: "Quality over volume", label: "Priorities", default: "clear and structured listings\nreal, useful information\nand more meaningful connections", multiline: true, list: true },
  { key: "about.quality.2", page: "about", section: "Quality over volume", label: "Paragraph 2", default: "By maintaining a higher standard for how listings are presented and how people connect, the experience becomes more valuable for everyone involved.", multiline: true },
  { key: "about.ahead.heading", page: "about", section: "Looking ahead", label: "Heading", default: "Looking ahead" },
  { key: "about.ahead.1", page: "about", section: "Looking ahead", label: "Paragraph 1", default: "As {city} continues to grow, the need for a more structured and transparent rental process becomes increasingly important.", multiline: true },
  { key: "about.ahead.2", page: "about", section: "Looking ahead", label: "Paragraph 2", default: "This platform is built to support that, making it easier to find the right homes and connect with the right people.", multiline: true },
  { key: "about.contact.heading", page: "about", section: "Get in touch", label: "Heading", default: "Get in touch" },
  { key: "about.contact.note", page: "about", section: "Get in touch", label: "Note above the form", default: "Questions, feedback, or partnership ideas? Send us a note, it goes straight to our team.", multiline: true },
  { key: "owners.hero.heading", page: "owners", section: "Sign-up step", label: "Heading", default: "List your property" },
  { key: "owners.hero.subtitle", page: "owners", section: "Sign-up step", label: "Subtitle", default: "List once. Stop answering the same questions. Create your free account to get started.", multiline: true },
  { key: "owners.badge.1", page: "owners", section: "Sign-up step", label: "Badge 1", default: "List once" },
  { key: "owners.badge.2", page: "owners", section: "Sign-up step", label: "Badge 2", default: "Quality leads" },
  { key: "owners.badge.3", page: "owners", section: "Sign-up step", label: "Badge 3", default: "No commissions" },
  { key: "owners.profile.eyebrow", page: "owners", section: "Profile step", label: "Eyebrow", default: "Final step" },
  { key: "owners.profile.heading", page: "owners", section: "Profile step", label: "Heading", default: "Complete your owner profile" },
  { key: "owners.profile.subtitle", page: "owners", section: "Profile step", label: "Subtitle", default: "Renters want to know who they're renting from. A complete profile builds trust and gets faster responses.", multiline: true },
  { key: "owners.profile.proof", page: "owners", section: "Profile step", label: "Proof line", default: "💡 Owners with complete profiles receive 3× more inquiries" },

  { key: "areas.hero.heading", page: "areas", section: "Intro", label: "Heading", default: "Where to live in {city}" },
  { key: "areas.hero.intro", page: "areas", section: "Intro", label: "Paragraph", multiline: true, default: "{siteName} covers {areaCount} areas in and around {city}, {region}. Each one has its own character, prices and distance to the beach, start here, then browse the homes available in the area that fits you." },
  { key: "pricing.heading", page: "pricing", section: "Intro", label: "Heading", default: "Extras" },
  { key: "pricing.intro", page: "pricing", section: "Intro", label: "Paragraph", multiline: true, default: "Browsing and contacting homes on {siteName} is free. These are optional." },
  { key: "pricing.notice", page: "pricing", section: "Notice", label: "Note under the prices", multiline: true, default: "", hint: "Empty by default and then not shown. Fill it only if a note about how prices are set becomes necessary in this market." },
];

// Social keys first, then page titles, then marketing copy: the admin lists
// them in this order. Never rename a key, stored rows are addressed by it.
// The investor deck's password page (/deck), edited in Admin → Fundraising →
// The deck → Deck settings (DeckLockCopy). It is shown before the password, so
// it can't live in the protected deck content. One text for every market.
export const DECK_LOCK_COPY: SiteCopySpec[] = [
  { key: "deck.lock.kicker", page: "deck", section: "Password page", label: "Small title", default: "Investor deck", maxLength: 40 },
  { key: "deck.lock.title", page: "deck", section: "Password page", label: "Title (*stars* for the coloured words)", default: "Homes for *months.*", maxLength: 80 },
  { key: "deck.lock.lead", page: "deck", section: "Password page", label: "Line under the title", default: "Enter the password from your invitation.", maxLength: 160 },
  { key: "deck.lock.button", page: "deck", section: "Password page", label: "Button", default: "Open the deck", maxLength: 40 },
];

// The tip box at the top of each owner page (Oct 15, Angelina: one or two
// lines on what to do there). Empty hides that tip for everyone.
export const OWNER_TIPS_COPY: SiteCopySpec[] = [
  { key: "tips.requests", page: "ownerTips", section: "Tips", label: "Requests", default: "Renters who want your dates. Accept the ones you like, decline the rest. A quick answer gets you more requests.", multiline: true, maxLength: 200 },
  { key: "tips.homes", page: "ownerTips", section: "Tips", label: "My homes", default: "Keep your calendar up to date and share your link in your groups. Those two things bring the most requests.", multiline: true, maxLength: 200 },
  { key: "tips.matched", page: "ownerTips", section: "Tips", label: "Matched renters", default: "Renters whose search fits your home. Send a request to the ones you like, or tell them all your home is available.", multiline: true, maxLength: 200 },
  { key: "tips.inbox", page: "ownerTips", section: "Tips", label: "Inbox", default: "Your chats with renters. Answer within a few hours: renters remember who replies.", multiline: true, maxLength: 200 },
  { key: "tips.calendar", page: "ownerTips", section: "Tips", label: "Calendar", default: "Block dates you're not available. Renters can only request open dates.", multiline: true, maxLength: 200 },
  { key: "tips.profile", page: "ownerTips", section: "Tips", label: "My profile", default: "This is what renters see about you. A photo and a few friendly lines get more yeses.", multiline: true, maxLength: 200 },
];

// The numbers bar on the home, owner and collection pages (backlog b29bf95c,
// Oct 17). The number comes from the database; these are the words under it.
export const STATS_COPY: SiteCopySpec[] = [
  { key: "stats.homes", page: "home", section: "Numbers bar", label: "Under the number of homes", default: "homes to rent", maxLength: 60, hint: "Shown once there are 20 homes or more." },
  { key: "stats.areas", page: "home", section: "Numbers bar", label: "Under the number of areas", default: "areas with homes", maxLength: 60, hint: "Shown once 3 areas have homes." },
  { key: "stats.answered", page: "home", section: "Numbers bar", label: "Under the number of answered requests", default: "renters got an answer in the last 90 days", maxLength: 60, hint: "Shown once 10 requests were answered." },
];

// The rules note (Oct 19, Felipe: a renter pitched a business in a request and
// said "maybe y'all should put that as a disclaimer"). Shown above Send on a
// listing's request form, and at the top of a chat until you write in it.
export const RULES_NOTE_COPY: SiteCopySpec[] = [
  { key: "request.rules-note", page: "messages", section: "Rules note", label: "On the request form", default: "This request is between you and the owner, and checked for spam and scams by our team and AI.", multiline: true, maxLength: 200, hint: "Followed by a Rules link." },
  { key: "chat.rules-note", page: "messages", section: "Rules note", label: "At the top of a chat", default: "This chat is between you and the owner, and checked for spam and scams by our team and AI.", multiline: true, maxLength: 200, hint: "Shown until you write in the chat. Followed by a Rules link." },
];

export const SITE_COPY: SiteCopySpec[] = [...SOCIAL_COPY, ...SEO_COPY, ...OWNER_PRICING_COPY, ...MARKETING_COPY, ...DECK_LOCK_COPY, ...OWNER_TIPS_COPY, ...STATS_COPY, ...RULES_NOTE_COPY];

export const SITE_COPY_BY_KEY: Record<string, SiteCopySpec> = Object.fromEntries(SITE_COPY.map((s) => [s.key, s]));

export const siteCopyForPage = (page: SiteCopyPage): SiteCopySpec[] => SITE_COPY.filter((s) => s.page === page);

// ---- Resolution -------------------------------------------------------------

export interface CopyVars {
  siteName: string;
  city: string;
  region: string;
  country: string;
  [extra: string]: string;
}

/** Fill {placeholders}; unknown ones stay verbatim so a typo is visible on the page. */
export const interpolateCopy = (template: string, vars: Partial<CopyVars>): string =>
  template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const v = vars[name];
    return typeof v === "string" ? v : match;
  });

/** Newline-separated bullets → trimmed, non-empty lines. */
export const splitCopyList = (value: string): string[] =>
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

export interface SiteCopyRowLike {
  key: string;
  tenant_id: string | null;
  value: string;
  updated_at?: string | null;
}

/** The row for `key` under `tenantId` (null = global), newest updated_at wins. */
export function pickSiteCopyRow<T extends SiteCopyRowLike>(
  rows: readonly T[] | undefined,
  key: string,
  tenantId: string | null,
): T | undefined {
  const ts = (r: T) => Date.parse(r.updated_at ?? "") || 0;
  let best: T | undefined;
  for (const r of rows ?? []) {
    if (r.key !== key || (r.tenant_id ?? null) !== (tenantId ?? null)) continue;
    if (!best || ts(r) > ts(best)) best = r;
  }
  return best;
}

export type CopySource = "tenant" | "global" | "default";

export interface ResolvedCopy {
  /** Interpolated text ready to render. */
  value: string;
  /** The stored or default template, placeholders intact (what the admin edits). */
  raw: string;
  source: CopySource;
  isDefault: boolean;
}

/** Tenant row → global row → catalog default, interpolated. Unknown keys resolve to "". */
export function resolveSiteCopy<T extends SiteCopyRowLike>(
  rows: readonly T[] | undefined,
  tenantId: string | null,
  key: string,
  vars: Partial<CopyVars>,
): ResolvedCopy {
  const tenantRow = tenantId ? pickSiteCopyRow(rows, key, tenantId) : undefined;
  const globalRow = pickSiteCopyRow(rows, key, null);
  const row = tenantRow ?? globalRow;
  const raw = row ? row.value : SITE_COPY_BY_KEY[key]?.default ?? "";
  return {
    value: interpolateCopy(raw, vars),
    raw,
    source: tenantRow ? "tenant" : globalRow ? "global" : "default",
    isDefault: !row,
  };
}

/** A share-text override wins only when it says something. */
export const resolveShareText = (o: { override: string | null | undefined; fallback: string }): string => {
  const override = o.override?.trim();
  return override ? override : o.fallback;
};

/** Where to check how a link renders after publishing. */
export const shareDebugLinks = (url: string) => ({
  facebook: `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(url)}`,
  linkedin: `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(url)}`,
  x: "https://cards-dev.twitter.com/validator",
});
